import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Radio,
  Tooltip,
  Tag,
  Typography,
  Checkbox,
  Descriptions,
  Select,
  Switch,
  message,
  Alert,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { format } from 'date-fns/format';
import Item from 'antd/lib/descriptions/Item';
import { QuestionCircleOutlined } from '@ant-design/icons';
import type { SearchableListItem } from '~/components/workflow/SearchableInputComponent';
import { SearchableInput } from '~/components/workflow/SearchableInputComponent';
import { useStoreState, useStoreActions } from '~/store';
import { BaseFeatureToggles } from '~/store/navigation/BaseFeatureToggles';
import type { RulePageForm } from './types';
import { CriteriaSelection, CRITERIA_SELECTION_TO_RULE_ACTION_TYPE, HazmatCriteriaOptions } from './types';
import {
  buildOriginCriteria,
  buildDestinationCountryCriteria,
  buildDestinationPostalCriteria,
  buildExactDateDeliveryCriteria,
  buildPackageWeightCriteria,
  buildPoBoxCriteria,
  buildShipmentTagCriteria,
  initializeEmptyForm,
  initializePopulatedForm,
  buildShipOptionCriteria,
  buildShipFromCountryCriteria,
  buildShipFromPostalCriteria,
  findCriteria,
  isCriteriaDisabled,
  buildHazardousCriteria,
  buildDestinationRegionCriteria,
  buildShipFromRegionCriteria,
} from './utils';
import { RuleDrafter } from './RuleDrafter';
import { RuleSetPostalCodeFormEntry } from '~/components/ruleSets/RuleSetPostalCodeFormEntry';
import { ShipOptionSelect } from '~/components/printLabelTool/sections/ShipOptions/ShipOptionSelect';
import { useLifecycle } from '~/hooks/useLifecycle';
import type { PartnerRuleCreate, PartnerTenant, PartnerRuleCriterionDefinition } from '@shipium-corp/shipium-ts-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSession } from '~/hooks/useSession';
import { OPT_INS } from '~/store/security/OptIns';
import { partnerManagementService } from '~/services';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';
import { useForm } from '~/hooks/useForm';
import { SelectCountry } from '~/components/form/SelectCountry';
import { Flex } from '~/components/Flex';
import { useFormArray } from '~/hooks/useFormArray';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useMutationInvalidator } from '~/hooks/useMutationInvalidator';
import { FloatingBar, FormButtons } from '~/components/form/FormButtons';
import { RuleImpactCountButton } from '~/components/ruleSets/RuleImpactCountButton';
import { ruleFormToFilter, formListType } from '~/utils/ruleImpactCount/ruleFormToFilter';
import type { RuleImpactFilterResult } from '~/utils/ruleImpactCount/ruleCriteriaToTypesenseFilter';
import { useFormStyles } from '~/components/form/formStyles';
// import { SelectRegion } from '~/components/form/SelectRegion';
const { Title, Text } = Typography;

export const RulePage = () => {
  const classes = useFormStyles();
  const navigate = useNavigate();
  const { sessionPartnerUrl, sessionPartnerId, isOptInEnabled, isShipiumView } = useSession();
  // The Rules Reforge tooling (rule draft, impact counts) is gated behind the RULES_AI
  // opt-in. Internal Shipium users always see it.
  const ruleToolsEnabled = isShipiumView || isOptInEnabled(OPT_INS.RULES_AI);
  const {
    form,
    submitForm,
    errorMessage,
    clearErrorMessage,
    touched,
    onValuesChange,
    clearTouched,
    setFormValue,
    setFormValues,
  } = useForm<Partial<RulePageForm>>();
  const { ruleSetId, ruleId } = useParams<{ ruleSetId: string; ruleId?: string }>();

  //#region Page State
  const [useOriginPostalOrCountry, setUseOriginPostalOrCountry] = useState(false);
  const [shipmentTagSearchValue, setShipmentTagSearchValue] = useState('');
  //#endregion

  //#region Rule Initialization
  const { data: rule } = useQuery({
    ...useQueryCreator(partnerManagementService.rules.get, {
      args: [ruleId!],
    }),
    enabled: Boolean(ruleId),
  });

  const { data: rulePropertyInfos } = useQuery({
    ...useQueryCreator(partnerManagementService.rules.rulePropertyInfos, {
      args: [],
    }),
    enabled: Boolean(ruleSetId),
  });

  const shipmentWeight = rulePropertyInfos?.find((ruleInfo) => {
    return ruleInfo?.ruleProperty?.rulePropertyEnum === 'weight';
  });
  //#endregion

  //#region Audit Info
  const { auditInfo, ruleUrn } = rule || {};
  const defaults = { lastUpdatedBy: '', createdBy: '', creationTimeStamp: '', lastUpdatedTimeStamp: '', version: 0 };
  const { lastUpdatedBy, lastUpdatedTimeStamp, createdBy, creationTimeStamp, version } = auditInfo || defaults;

  const { data: lastUpdatedByUser } = useQuery({
    ...useQueryCreator(partnerManagementService.users.get, {
      args: [lastUpdatedBy!],
    }),
    enabled: Boolean(lastUpdatedBy),
  });

  const { data: createdByUser } = useQuery({
    ...useQueryCreator(partnerManagementService.users.get, {
      args: [createdBy!],
    }),
    enabled: Boolean(createdBy),
  });

  const formattedCreatedBy = createdByUser ? `${createdByUser.givenName} ${createdByUser.surname}` : 'N/A';
  const formattedLastUpdatedBy = lastUpdatedByUser
    ? `${lastUpdatedByUser.givenName} ${lastUpdatedByUser.surname}`
    : 'N/A';

  //#region Tenant Initialization
  const fetchTenants = useStoreActions((actions) => actions.tenant.fetchTenants);
  const tenants = useStoreState((state) => state.tenant.tenants);
  const usesTenant =
    useStoreState((state) => state.partner.activePartner)?.featureToggles[BaseFeatureToggles.TENANT] === 'true';

  useEffect(() => {
    if (usesTenant) fetchTenants({});
  }, [fetchTenants, usesTenant]);

  const initialTenants: PartnerTenant[] = [];

  rule?.tenantIds?.forEach((tenantId) => {
    if (tenants[tenantId]) {
      initialTenants.push(tenants[tenantId]);
    }
  });
  //#endregion

  const { data: origins } = useQuery({
    ...useQueryCreator(partnerManagementService.origins.search, {
      args: [],
    }),
    enabled: Boolean(sessionPartnerUrl),
  });

  //#region Carrier Service Initialization
  const fetchCarrierList = useStoreActions((actions) => actions.carrier.fetchCarrierList);
  const fetchFulfillmentContexts = useStoreActions(
    (actions) => actions.FP_fulfillmentContext.fetchFulfillmentContextDetails,
  );
  const fetchUsedV2Carriers = useStoreActions((actions) => actions.carrier.fetchUsedV2Carriers);
  const usedV2Carriers = useStoreState((state) => state.carrier.usedV2Carriers);
  const [usedCarriersLoading, setUsedCarriersLoading] = useState(false);

  useLifecycle({
    onMount: () => {
      fetchCarrierList();
      fetchFulfillmentContexts();
    },
  });

  useEffect(() => {
    setUsedCarriersLoading(true);
    fetchUsedV2Carriers().then(() => {
      setUsedCarriersLoading(false);
    });
  }, [fetchUsedV2Carriers, setUsedCarriersLoading]);

  const carrierServiceMethodSelectionsV2 = Object.entries(usedV2Carriers).reduce<SearchableListItem[]>(
    (accumulator, currentValue) => {
      return [
        ...accumulator,
        {
          value: currentValue?.[0],
          displayValue: `${currentValue[1].name} - (All)`,
          description: `Select to specifiy all shipping options provided by ${currentValue[1].name}`,
        },
        ...Object.entries(currentValue[1].methods).map((method) => {
          return {
            value: method[0],
            displayValue: method[1].name,
            description: `Select to specifiy "${method[1].name}" provided by "${currentValue[1].name}"`,
          };
        }),
      ];
    },
    [],
  );

  //#endregion

  const bootstrapData = () => {
    if (rule) {
      const existingOriginPostalRule = findCriteria(rule.criteria, 'origin_postal');
      const existingOriginCountryRule = findCriteria(rule.criteria, 'origin_country');
      const existingOriginRegionRule = findCriteria(rule.criteria, 'origin_region');

      setUseOriginPostalOrCountry(
        Boolean(existingOriginPostalRule || existingOriginCountryRule || existingOriginRegionRule),
      );

      initializePopulatedForm(form, rule);
    } else {
      initializeEmptyForm(form);
    }
  };

  useEffect(() => {
    bootstrapData();
    // eslint-disable-next-line
  }, [rule]);

  const createRuleAndLink = useMutation(
    useMutationInvalidator(async (rule: PartnerRuleCreate) => {
      return partnerManagementService.ruleSets.createAndAddRule({ ruleSetId: ruleSetId!, rule });
    }),
  );

  const updateRule = useMutation(useMutationInvalidator(partnerManagementService.rules.update));

  const handleSubmit = async (values: Partial<RulePageForm>) => {
    const criteria: PartnerRuleCriterionDefinition[] = [
      buildOriginCriteria(values),
      buildShipFromCountryCriteria(values),
      buildShipFromRegionCriteria(values),
      buildShipFromPostalCriteria(values),
      buildDestinationCountryCriteria(values),
      buildDestinationRegionCriteria(values),
      buildDestinationPostalCriteria(values),
      buildShipmentTagCriteria(values),
      buildPackageWeightCriteria(values),
      buildPoBoxCriteria(values),
      buildExactDateDeliveryCriteria(values),
      buildShipOptionCriteria(values),
      buildHazardousCriteria(values),
    ].filter((criteria): criteria is PartnerRuleCriterionDefinition => criteria !== null);

    try {
      if (rule) {
        const { criteria: _, ...ruleWithoutCriteria } = rule;

        await updateRule.mutateAsync({
          ruleId: ruleId!,
          payload: {
            ...ruleWithoutCriteria,
            name: values.name || '',
            description: values.description || '',
            carrierAndServiceMethodList: values.eligibleCarrierServiceMethods || [],
            listType: CRITERIA_SELECTION_TO_RULE_ACTION_TYPE[values.carrierSelectionCriteria!],
            tenantIds: values?.tenants || [],
            criteria,
            active: rule?.active || false,
            archived: rule?.archived || false,
          },
        });

        message.success('Rule updated successfully');
        clearTouched();
        clearErrorMessage();
      } else {
        await createRuleAndLink.mutateAsync({
          active: false,
          name: values.name || '',
          description: values.description || '',
          carrierAndServiceMethodList: values.eligibleCarrierServiceMethods || [],
          listType: CRITERIA_SELECTION_TO_RULE_ACTION_TYPE[values.carrierSelectionCriteria!],
          tenantIds: values?.tenants || [],
          criteria,
        });
        navigate(`/${sessionPartnerUrl}/carrier-selection/rulesets`);
        message.success('Rule created successfully');
      }

      return { success: true };
    } catch (error) {
      const isPayloadTooLarge = error.message?.includes('payload is too large');
      if (isPayloadTooLarge) {
        return {
          success: false,
          errorMessage: (
            <>
              The request payload is too large. Carrier Selection Rules are designed for exception-based scenarios
              rather than large data sets (e.g., extensive postal code lists). Please{' '}
              <a
                href="https://docs.shipium.com/docs/contacting-shipium-support"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact support
              </a>{' '}
              to discuss alternative approaches for your routing requirements.
            </>
          ),
        };
      }
      return { success: false, errorMessage: error.message };
    }
  };

  const tenantCriteria = Form.useWatch('tenantCriteria', form);
  const originCriteria = Form.useWatch('originCriteria', form);
  const shipFromCriteria = Form.useWatch('shipFromCriteria', form);
  const useShipFromCountry = Form.useWatch('useShipFromCountry', form);
  // const useShipFromRegion = Form.useWatch('useShipFromRegion', form);
  const useShipFromPostalCodes = Form.useWatch('useShipFromPostalCodes', form);
  const destinationCriteria = Form.useWatch('destinationCriteria', form);
  const useCountry = Form.useWatch('useCountry', form);
  // const useRegion = Form.useWatch('useRegion', form);
  const usePostalCodes = Form.useWatch('usePostalCodes', form);
  const packageCriteria = Form.useWatch('packageCriteria', form);
  const poBoxCriteria = Form.useWatch('poBoxCriteria', form);
  const shipmentTagCriteria = Form.useWatch('shipmentTagCriteria', form);
  const carrierSelectionCriteria = Form.useWatch('carrierSelectionCriteria', form);

  const getFormFilterResult = useCallback(
    (): RuleImpactFilterResult => ruleFormToFilter(form.getFieldsValue()),
    [form],
  );

  const {
    value: shipmentTags,
    add: addShipmentTag,
    remove: removeShipmentTag,
  } = useFormArray<string>({
    field: 'shipmentTags',
    form,
  });

  const shipOptionCriteria = Form.useWatch('shipOptionCriteria', form);
  const hazardousCriteria = Form.useWatch('hazardousCriteria', form);

  const {
    value: originPostalCodes,
    add: addOriginPostalCode,
    remove: removeOriginPostalCode,
  } = useFormArray<string>({
    field: 'originPostalCodes',
    form,
  });

  const {
    value: postalCodes,
    add: addPostalCode,
    remove: removePostalCode,
  } = useFormArray<string>({
    field: 'postalCodes',
    form,
  });

  return (
    <Form layout="vertical" form={form} onValuesChange={onValuesChange} className={classes.form}>
      <Flex direction="vertical" gap="lg" justifySelf="center">
        {!ruleId && ruleToolsEnabled && (
          <RuleDrafter
            form={form}
            partnerId={sessionPartnerId}
            availableCarriers={carrierServiceMethodSelectionsV2
              .filter((c): c is typeof c & { displayValue: string } => typeof c.displayValue === 'string')
              .map((c) => ({ id: c.value, name: c.displayValue }))}
            availableTenants={Object.values(tenants)
              .filter((t) => t.active && !t.archived && t.tenantId && t.name)
              .map((t) => ({ id: t.tenantId!, name: t.name! }))}
            availableOrigins={Object.values(origins ?? [])
              .filter((o) => o.active && !o.archived && o.originId && o.name)
              .map((o) => ({ id: o.originId, name: o.name }))}
            onApplied={(draftedRule) => {
              // The "Use Ship From" toggle lives outside the form, so initializePopulatedForm
              // can't flip it. If the draft has any origin-postal/country/region criterion,
              // expand the Ship From section so the user can see the populated values.
              const hasShipFromCriterion = ['origin_postal', 'origin_country', 'origin_region'].some(
                (prop) => findCriteria(draftedRule.criteria, prop as Parameters<typeof findCriteria>[1]) !== undefined,
              );
              if (hasShipFromCriterion) {
                setUseOriginPostalOrCountry(true);
              }
            }}
          />
        )}
        <Flex direction="vertical" gap="sm">
          <Title level={3}>Carrier Selection Rule Details</Title>
          <Card>
            <Flex direction="vertical">
              <Title level={4}>Details</Title>
              <Form.Item
                label="Name"
                style={{ marginBottom: 10 }}
                name="name"
                rules={[{ required: true, message: 'Name required' }]}
              >
                <Input required={true} />
              </Form.Item>
              <Form.Item label="Description" style={{ marginBottom: 10 }} name="description">
                <TextArea />
              </Form.Item>
            </Flex>
          </Card>

          {usesTenant && (
            <Card>
              <Flex direction="vertical">
                <Title level={4}>Tenants</Title>
                <Form.Item name="tenantCriteria" label="Tenant criteria">
                  <Radio.Group>
                    <Radio
                      value={CriteriaSelection.DISABLED}
                      onClick={() => {
                        setFormValue('tenants', []);
                      }}
                    >
                      All tenants
                    </Radio>
                    <Radio value={CriteriaSelection.SPECIFIC}>Specified tenants</Radio>
                  </Radio.Group>
                </Form.Item>

                {!isCriteriaDisabled(tenantCriteria) && (
                  <Form.Item
                    name="tenants"
                    label="Tenants"
                    rules={[
                      (form) => {
                        if (form.getFieldValue('tenantCriteria') === CriteriaSelection.SPECIFIC) {
                          return { required: true, message: 'Tenants required' };
                        }

                        return { required: false };
                      },
                    ]}
                  >
                    <SearchableInput
                      multiple={true}
                      searchList={Object.values(tenants)
                        .filter((tenant) => tenant.active && !tenant.archived)
                        .map((tenant) => {
                          return {
                            value: tenant.tenantId!,
                            displayValue: tenant.name!,
                            description: tenant.description!,
                          };
                        })}
                    />
                  </Form.Item>
                )}
              </Flex>
            </Card>
          )}
        </Flex>

        <Flex direction="vertical" gap="sm">
          <Title level={3}>Selection Criteria</Title>

          <Card>
            <Flex direction="vertical">
              <Flex direction="horizontal" gap="xs">
                <Tooltip title="If you’re using At Large fulfillment contexts and need to apply postal code rules without a specific origin, please enable this toggle.">
                  <QuestionCircleOutlined style={{ marginRight: 5, marginTop: 3 }} />
                </Tooltip>
                Use Ship From Country/Region/Postal Codes
                <Switch
                  checked={useOriginPostalOrCountry}
                  onChange={(checked) => {
                    setUseOriginPostalOrCountry(checked);
                  }}
                />
              </Flex>
              {!useOriginPostalOrCountry ? (
                <>
                  <Title level={4}>Origin Matching</Title>
                  <Form.Item name="originCriteria" label="Origin criteria">
                    <Radio.Group>
                      <Radio
                        value={CriteriaSelection.DISABLED}
                        onClick={() => {
                          setFormValue('origins', []);
                        }}
                      >
                        All shipments are eligible, regardless of origin
                      </Radio>
                      <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                        ONLY INCLUDE shipments from specified origin(s)
                      </Radio>
                      <Radio value={CriteriaSelection.OMIT_ONLY}>
                        DO NOT INCLUDE shipments from specified origin(s)
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  {!isCriteriaDisabled(originCriteria) && (
                    <Form.Item label="Origins" name="origins" rules={[{ required: true, message: 'Origins required' }]}>
                      <SearchableInput
                        multiple={true}
                        searchList={Object.values(origins ?? [])
                          .filter((origin) => origin.active && !origin.archived)
                          .map((origin) => {
                            return {
                              value: origin.originId,
                              displayValue: origin.name,
                              description: origin.description,
                            };
                          })}
                      />
                    </Form.Item>
                  )}
                </>
              ) : (
                <>
                  <Title level={4}>Ship From Matching</Title>
                  <Form.Item name="shipFromCriteria" label="Ship from criteria">
                    <Radio.Group>
                      <Radio
                        value={CriteriaSelection.DISABLED}
                        onClick={() => {
                          setFormValues({
                            useShipFromCountry: false,
                            useShipFromRegion: false,
                            useShipFromPostalCodes: false,
                            originCountryCriteria: CriteriaSelection.DISABLED,
                            originRegionCriteria: CriteriaSelection.DISABLED,
                            originPostalCriteria: CriteriaSelection.DISABLED,
                            originCountry: [],
                            originRegion: [],
                            originPostalCodes: [],
                          });
                        }}
                      >
                        All shipments are eligible, regardless of ship from location
                      </Radio>
                      <Radio value={CriteriaSelection.SPECIFIC}>Specified countries, regions, and postal codes</Radio>
                    </Radio.Group>
                  </Form.Item>
                  {!isCriteriaDisabled(shipFromCriteria) && (
                    <>
                      <Card size="small">
                        <Flex direction="vertical" gap="xs">
                          <Form.Item name="useShipFromCountry" valuePropName="checked">
                            <Checkbox
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setFormValue('originCountryCriteria', CriteriaSelection.INCLUDE_ONLY);
                                } else {
                                  setFormValue('originCountryCriteria', CriteriaSelection.DISABLED);
                                  setFormValue('originCountry', []);
                                }
                              }}
                            >
                              <Title level={5}>Use ship from countries</Title>
                            </Checkbox>
                          </Form.Item>
                          <Form.Item name="originCountryCriteria">
                            <Radio.Group disabled={useShipFromCountry === false}>
                              <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                                ONLY INCLUDE countries matching these tags
                              </Radio>
                              <Radio value={CriteriaSelection.OMIT_ONLY}>
                                DO NOT INCLUDE countries matching these tags
                              </Radio>
                            </Radio.Group>
                          </Form.Item>
                          <Form.Item
                            label="Countries"
                            name="originCountry"
                            rules={useShipFromCountry ? [{ required: true, message: 'Countries required' }] : []}
                          >
                            <SelectCountry
                              disabled={useShipFromCountry === false}
                              multiple
                              placeholder="Specify countries"
                            />
                          </Form.Item>
                        </Flex>
                      </Card>

                      {/* <Card size="small">
                        <Flex direction="vertical" gap="xs">
                          <Form.Item name="useShipFromRegion" valuePropName="checked">
                            <Checkbox
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setFormValue('originRegionCriteria', CriteriaSelection.INCLUDE_ONLY);
                                } else {
                                  setFormValue('originRegionCriteria', CriteriaSelection.DISABLED);
                                  setFormValue('originRegion', []);
                                }
                              }}
                            >
                              <Title level={5}>Use ship from regions</Title>
                            </Checkbox>
                          </Form.Item>
                          <Form.Item name="originRegionCriteria">
                            <Radio.Group disabled={useShipFromRegion === false}>
                              <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                                ONLY INCLUDE regions matching these tags
                              </Radio>
                              <Radio value={CriteriaSelection.OMIT_ONLY}>
                                DO NOT INCLUDE regions matching these tags
                              </Radio>
                            </Radio.Group>
                          </Form.Item>
                          <Form.Item
                            label="Regions"
                            name="originRegion"
                            rules={useShipFromRegion ? [{ required: true, message: 'Regions required' }] : []}
                          >
                            <SelectRegion
                              multiple
                              placeholder="Specify regions"
                              disabled={useShipFromRegion === false}
                            />
                          </Form.Item>
                        </Flex>
                      </Card> */}

                      <Card size="small">
                        <Flex direction="vertical" gap="xs">
                          <Form.Item name="useShipFromPostalCodes" valuePropName="checked">
                            <Checkbox
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setFormValue('originPostalCriteria', CriteriaSelection.INCLUDE_ONLY);
                                } else {
                                  setFormValues({
                                    originPostalCriteria: CriteriaSelection.DISABLED,
                                    originPostalCodes: [],
                                  });
                                }
                              }}
                            >
                              <Title level={5}>Use ship from postal codes</Title>
                            </Checkbox>
                          </Form.Item>
                          <Form.Item name="originPostalCriteria">
                            <Radio.Group disabled={useShipFromPostalCodes === false}>
                              <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                                ONLY INCLUDE shipments from specified postal code(s)
                              </Radio>
                              <Radio value={CriteriaSelection.OMIT_ONLY}>
                                DO NOT INCLUDE shipments from specified postal code(s)
                              </Radio>
                            </Radio.Group>
                          </Form.Item>
                          <RuleSetPostalCodeFormEntry
                            formFieldName="originPostalCodes"
                            handleClear={() => {
                              setFormValue('originPostalCodes', []);
                            }}
                            handlePostalCodeEntry={(values) => addOriginPostalCode(values)}
                            disabled={useShipFromPostalCodes === false}
                            postalCodes={originPostalCodes ?? []}
                            handlePostalCodeClose={(value) => removeOriginPostalCode(value)}
                            rules={useShipFromPostalCodes ? [{ required: true, message: 'Postal codes required' }] : []}
                          />
                        </Flex>
                      </Card>
                    </>
                  )}
                </>
              )}
            </Flex>
          </Card>

          <Card>
            <Flex direction="vertical">
              <Title level={4}>Destination Matching</Title>
              <Form.Item name="destinationCriteria" label="Destination criteria">
                <Radio.Group>
                  <Radio
                    value={CriteriaSelection.DISABLED}
                    onClick={() => {
                      setFormValues({
                        useCountry: false,
                        useRegion: false,
                        usePostalCodes: false,
                        countryCriteria: CriteriaSelection.DISABLED,
                        regionCriteria: CriteriaSelection.DISABLED,
                        postalCodeCriteria: CriteriaSelection.DISABLED,
                        country: [],
                        region: [],
                        postalCodes: [],
                      });
                    }}
                  >
                    All destinations
                  </Radio>
                  <Radio value={CriteriaSelection.SPECIFIC}>Specified countries, regions, and postal codes</Radio>
                </Radio.Group>
              </Form.Item>
              {!isCriteriaDisabled(destinationCriteria) && (
                <>
                  <Card size="small">
                    <Flex direction="vertical" gap="xs">
                      <Form.Item name="useCountry" valuePropName="checked">
                        <Checkbox
                          onChange={(event) => {
                            if (event.target.checked) {
                              setFormValue('countryCriteria', CriteriaSelection.INCLUDE_ONLY);
                            } else {
                              setFormValues({
                                countryCriteria: CriteriaSelection.DISABLED,
                                country: [],
                              });
                            }
                          }}
                        >
                          <Title level={5}>Use destination countries</Title>
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="countryCriteria">
                        <Radio.Group disabled={useCountry === false}>
                          <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                            ONLY INCLUDE countries matching these tags
                          </Radio>
                          <Radio value={CriteriaSelection.OMIT_ONLY}>
                            DO NOT INCLUDE countries matching these tags
                          </Radio>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item
                        label="Countries"
                        name="country"
                        rules={useCountry ? [{ required: true, message: 'Countries required' }] : []}
                      >
                        <SelectCountry multiple placeholder="Specify countries" disabled={useCountry === false} />
                      </Form.Item>
                    </Flex>
                  </Card>

                  {/* <Card size="small">
                    <Flex direction="vertical" gap="xs">
                      <Form.Item name="useRegion" valuePropName="checked">
                        <Checkbox
                          onChange={(event) => {
                            if (event.target.checked) {
                              setFormValue('regionCriteria', CriteriaSelection.INCLUDE_ONLY);
                            } else {
                              setFormValues({
                                regionCriteria: CriteriaSelection.DISABLED,
                                region: [],
                              });
                            }
                          }}
                        >
                          <Title level={5}>Use destination regions</Title>
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="regionCriteria">
                        <Radio.Group disabled={useRegion === false}>
                          <Radio value={CriteriaSelection.INCLUDE_ONLY}>ONLY INCLUDE regions matching these tags</Radio>
                          <Radio value={CriteriaSelection.OMIT_ONLY}>DO NOT INCLUDE regions matching these tags</Radio>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item
                        label="Regions"
                        name="region"
                        rules={useRegion ? [{ required: true, message: 'Regions required' }] : []}
                      >
                        <SelectRegion multiple placeholder="Specify regions" disabled={useRegion === false} />
                      </Form.Item>
                    </Flex>
                  </Card> */}

                  <Card size="small">
                    <Flex direction="vertical" gap="xs">
                      <Form.Item name="usePostalCodes" valuePropName="checked">
                        <Checkbox
                          onChange={(event) => {
                            if (event.target.checked) {
                              setFormValue('postalCodeCriteria', CriteriaSelection.INCLUDE_ONLY);
                            } else {
                              setFormValues({
                                postalCodeCriteria: CriteriaSelection.DISABLED,
                                postalCodes: [],
                              });
                            }
                          }}
                        >
                          <Title level={5}>Use destination postal codes</Title>
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="postalCodeCriteria">
                        <Radio.Group disabled={usePostalCodes === false}>
                          <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                            ONLY INCLUDE shipments going to these postal codes
                          </Radio>
                          <Radio value={CriteriaSelection.OMIT_ONLY}>
                            DO NOT INCLUDE shipments going to these postal codes
                          </Radio>
                        </Radio.Group>
                      </Form.Item>
                      <RuleSetPostalCodeFormEntry
                        formFieldName="postalCodes"
                        handleClear={() => {
                          setFormValue('postalCodes', []);
                        }}
                        handlePostalCodeEntry={(values) => addPostalCode(values)}
                        disabled={usePostalCodes === false}
                        postalCodes={postalCodes ?? []}
                        handlePostalCodeClose={(value) => removePostalCode(value)}
                        rules={usePostalCodes ? [{ required: true, message: 'Postal codes required' }] : []}
                      />
                    </Flex>
                  </Card>
                </>
              )}
            </Flex>
          </Card>

          <Card>
            <Flex direction="vertical">
              <Title level={4}>Package Matching</Title>
              <Form.Item name="packageCriteria" label="Package criteria">
                <Radio.Group>
                  <Radio
                    value={CriteriaSelection.DISABLED}
                    onClick={() => {
                      setFormValues({
                        packageWeightComparator: '',
                        packageValue: '',
                        packageWeightUnitValue: '',
                      });
                    }}
                  >
                    All packages are eligible, regardless of size and weight
                  </Radio>
                  <Radio
                    value={CriteriaSelection.SPECIFIC}
                    onClick={() => {
                      setFormValues({
                        packageWeightComparator: form.getFieldValue('packageWeightComparator') || 'gte',
                        packageValue: form.getFieldValue('packageValue') || '5',
                        packageWeightUnitValue: form.getFieldValue('packageWeightUnitValue') || 'oz',
                      });
                    }}
                  >
                    Specify required package criteria
                  </Radio>
                </Radio.Group>
              </Form.Item>
              {!isCriteriaDisabled(packageCriteria) && (
                <Flex direction="horizontal" gap="xs" alignItems="center">
                  <Text>Match all shipments that are </Text>
                  <Form.Item name={'packageWeightComparator'}>
                    <Select
                      options={shipmentWeight?.ruleComparatorType?.availableRuleComparators?.map((comparator) => {
                        return {
                          value: comparator.ruleComparatorEnum,
                          label: comparator.ruleComparatorName,
                        };
                      })}
                    />
                  </Form.Item>
                  <Text>a weight of</Text>
                  <Form.Item name="packageValue">
                    <Input />
                  </Form.Item>
                  <Form.Item name={'packageWeightUnitValue'}>
                    <Select
                      options={shipmentWeight?.unitType?.availableScalarUnits?.map((unitType) => {
                        return {
                          value: unitType.scalarUnitEnum,
                          label: unitType.scalarUnitName,
                        };
                      })}
                    />
                  </Form.Item>
                </Flex>
              )}
            </Flex>
          </Card>

          <Card>
            <Flex direction="vertical">
              <Title level={4}>PO Box Matching</Title>
              <Form.Item name="includeHcAndRr" valuePropName="checked">
                <Checkbox>Include HC (Highway Contract) and RR (Rural Route) Boxes</Checkbox>
              </Form.Item>
              <Form.Item name="poBoxCriteria" label="PO box criteria">
                <Radio.Group>
                  <Radio value={CriteriaSelection.DISABLED}>
                    All shipments are eligible, regardless if shipment is for a PO Box
                  </Radio>
                  <Radio value={CriteriaSelection.INCLUDE_ONLY}>ONLY INCLUDE shipments destined for a PO Box</Radio>
                  <Radio value={CriteriaSelection.OMIT_ONLY}>DO NOT INCLUDE shipments destined for a PO Box</Radio>
                </Radio.Group>
              </Form.Item>
            </Flex>
          </Card>

          <Card>
            <Flex direction="vertical">
              <Title level={4}>Tag Matching</Title>
              <Form.Item name="shipmentTagCriteria" label="Tag criteria">
                <Radio.Group>
                  <Radio
                    value={CriteriaSelection.DISABLED}
                    onClick={() => {
                      setFormValue('shipmentTags', []);
                    }}
                  >
                    All shipments are eligible, regardless of tag
                  </Radio>
                  <Radio value={CriteriaSelection.INCLUDE_ONLY}>ONLY INCLUDE shipments matching these tags</Radio>
                  <Radio value={CriteriaSelection.OMIT_ONLY}>DO NOT INCLUDE shipments matching these tags</Radio>
                </Radio.Group>
              </Form.Item>
              {!isCriteriaDisabled(shipmentTagCriteria) && (
                <>
                  <Text>
                    Shipment tags{' '}
                    <Tooltip
                      title={
                        'Shipment tags are decorated on shipments at request time. They are string keys that allow a partner to categorize types of shipments.'
                      }
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Text>
                  <Input.Search
                    value={shipmentTagSearchValue}
                    onChange={(e) => {
                      setShipmentTagSearchValue(e.target.value);
                    }}
                    placeholder="Specify shipment tags"
                    allowClear
                    enterButton="Add"
                    onSearch={(value, e) => {
                      e?.preventDefault();
                      addShipmentTag(value);
                      setShipmentTagSearchValue('');
                    }}
                  />
                  <Form.Item name="shipmentTags" rules={[{ required: true, message: 'Shipment tags required' }]}>
                    {shipmentTags?.map((tagEntry, index) => {
                      return (
                        <Tag
                          key={index}
                          closable
                          style={{ overflow: 'hidden' }}
                          onClose={(e) => {
                            e.preventDefault();
                            removeShipmentTag(tagEntry);
                          }}
                        >
                          {tagEntry}
                        </Tag>
                      );
                    })}
                  </Form.Item>
                </>
              )}
            </Flex>
          </Card>

          <Card>
            <Flex direction="vertical">
              <Title level={4}>Exact Delivery Date Matching</Title>
              <Form.Item name="exactDateDeliveryCriteria" label="Exact delivery date criteria">
                <Radio.Group>
                  <Radio value={CriteriaSelection.DISABLED}>
                    All shipments are eligible, do not consider exact delivery date
                  </Radio>
                  <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                    ONLY INCLUDE shipments with an exact delivery date
                  </Radio>
                  <Radio value={CriteriaSelection.OMIT_ONLY}>
                    DO NOT INCLUDE shipments with an exact delivery date
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Flex>
          </Card>

          <Card>
            <Flex direction="vertical">
              <Title level={4}>Ship Option Matching</Title>
              <Form.Item name="shipOptionCriteria" label="Ship option criteria">
                <Radio.Group>
                  <Radio value={CriteriaSelection.DISABLED}>
                    All shipments are eligible, regardless of ship option
                  </Radio>
                  <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                    ONLY INCLUDE shipments matching these ship options
                  </Radio>
                  <Radio value={CriteriaSelection.OMIT_ONLY}>
                    DO NOT INCLUDE shipments matching these ship options
                  </Radio>
                </Radio.Group>
              </Form.Item>
              {!isCriteriaDisabled(shipOptionCriteria) && (
                <Form.Item
                  name="shipOptions"
                  label="Ship Options"
                  rules={[{ required: true, message: 'Ship options required' }]}
                >
                  <ShipOptionSelect mode="multiple" />
                </Form.Item>
              )}
            </Flex>
          </Card>

          <Card>
            <Flex direction="vertical">
              <Title level={4}>Hazardous Materials Matching</Title>
              <Form.Item name="hazardousCriteria" label="Hazardous materials criteria">
                <Radio.Group>
                  <Radio
                    value={CriteriaSelection.DISABLED}
                    onClick={() => {
                      setFormValues({
                        hazardousCriteriaOptions: HazmatCriteriaOptions.NONE,
                      });
                    }}
                  >
                    All shipments are eligible, regardless of hazardous materials
                  </Radio>
                  <Radio
                    value={CriteriaSelection.INCLUDE_ONLY}
                    onClick={() => {
                      setFormValues({
                        hazardousCriteriaOptions:
                          form.getFieldValue('hazardousCriteriaOptions') || HazmatCriteriaOptions.LIMITED_QUANTITY,
                      });
                    }}
                  >
                    ONLY INCLUDE shipments matching these hazardous materials
                  </Radio>
                  <Radio
                    value={CriteriaSelection.OMIT_ONLY}
                    onClick={() => {
                      setFormValues({
                        hazardousCriteriaOptions:
                          form.getFieldValue('hazardousCriteriaOptions') || HazmatCriteriaOptions.LIMITED_QUANTITY,
                      });
                    }}
                  >
                    DO NOT INCLUDE shipments matching these hazardous materials
                  </Radio>
                </Radio.Group>
              </Form.Item>
              {!isCriteriaDisabled(hazardousCriteria) && (
                <Form.Item label="Hazardous materials options" name="hazardousCriteriaOptions">
                  <Radio.Group disabled={isCriteriaDisabled(hazardousCriteria)}>
                    <Radio value={HazmatCriteriaOptions.LIMITED_QUANTITY}>Limited Quantity Hazmat</Radio>
                    <Radio value={HazmatCriteriaOptions.FULL_HAZMAT}>Full Hazmat</Radio>
                    <Radio value={HazmatCriteriaOptions.HAZMAT}>Full Hazmat or Limited Quantity Hazmat</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            </Flex>
          </Card>
        </Flex>

        <Flex direction="vertical" gap="sm">
          <Title level={3}>Carrier Criteria</Title>

          <Card loading={usedCarriersLoading && Object.keys(usedV2Carriers ?? {}).length === 0}>
            <Flex direction="vertical">
              <Title level={4}>Eligible Carriers & Methods</Title>
              <Form.Item name="carrierSelectionCriteria" label="Carrier selection criteria">
                <Radio.Group>
                  <Flex direction="vertical" gap="sm">
                    <Radio value={CriteriaSelection.INCLUDE_ONLY}>
                      <Flex direction="vertical" gap="none">
                        <Text>USE ONLY these carriers</Text>
                        <Text type="secondary">Replaces the current list of eligible carriers with your selection</Text>
                      </Flex>
                    </Radio>
                    <Radio value={CriteriaSelection.ADD}>
                      <Flex direction="vertical" gap="none">
                        <Text>RESTORE these carriers</Text>
                        <Text type="secondary">
                          Re-includes your selected carriers if they were removed by a previous rule. Only carriers
                          passed in the original carrier selection request can be restored.
                        </Text>
                      </Flex>
                    </Radio>
                    <Radio value={CriteriaSelection.OMIT_ONLY}>
                      <Flex direction="vertical" gap="none">
                        <Text>REMOVE these carriers</Text>
                        <Text type="secondary">
                          Removes your selected carriers from the current list of eligible carriers
                        </Text>
                      </Flex>
                    </Radio>
                  </Flex>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label={
                  <Text>
                    Eligible carrier methods{' '}
                    <Tooltip
                      title={
                        "You can specify both carriers and specific carrier methods. Selecting a carrier means that all service methods will be considered as part of the rule's evaluation."
                      }
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Text>
                }
                name={'eligibleCarrierServiceMethods'}
                rules={[{ required: true, message: 'One or more carrier service methods must be specified.' }]}
              >
                <SearchableInput multiple={true} searchList={carrierServiceMethodSelectionsV2} />
              </Form.Item>
            </Flex>
          </Card>

          {ruleId && (
            <Card>
              <Flex direction="vertical">
                <Title level={4}>Audit Info</Title>
                <Descriptions layout={'horizontal'} size={'default'} column={2}>
                  <Item label={<b>URN</b>}>
                    <Text copyable={{ text: ruleUrn }}>{ruleUrn}</Text>
                  </Item>
                  <Item label={<b>Version</b>}>{version}</Item>
                  <Item label={<b>Created By</b>}>{formattedCreatedBy}</Item>
                  <Item label={<b>Creation Timestamp</b>}>
                    {(creationTimeStamp && format(new Date(creationTimeStamp), 'PPPP')) || 'N/A'}
                  </Item>
                  <Item label={<b>Last Updated By</b>}>{formattedLastUpdatedBy}</Item>
                  <Item label={<b>Last Updated Timestamp</b>}>
                    {(lastUpdatedTimeStamp && format(new Date(lastUpdatedTimeStamp), 'PPPP')) || 'N/A'}
                  </Item>
                </Descriptions>
              </Flex>
            </Card>
          )}

          {errorMessage && (
            <Alert
              closable
              afterClose={clearErrorMessage}
              message={`Rule ${ruleId ? 'update' : 'creation'} failure`}
              description={errorMessage}
              type="error"
              showIcon
            />
          )}
        </Flex>

        <FloatingBar>
          <FormButtons
            onBack={() => navigate(-1)}
            onUndo={
              ruleId
                ? () => {
                    form.resetFields();
                    bootstrapData();
                    clearTouched();
                  }
                : undefined
            }
            onSubmitLabel="Save as Draft"
            onSubmit={() => submitForm(handleSubmit)}
            isLoading={createRuleAndLink.isLoading || updateRule.isLoading}
            touched={touched}
            extraActions={
              ruleToolsEnabled ? (
                <RuleImpactCountButton
                  getFilterResult={getFormFilterResult}
                  listType={formListType(carrierSelectionCriteria)}
                  disabled={touched === false || createRuleAndLink.isLoading || updateRule.isLoading}
                />
              ) : undefined
            }
          />
        </FloatingBar>
      </Flex>
    </Form>
  );
};

export default RulePage;
